import { getRegularViewTableColumns } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/regularViewTableColumns';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	BlockRange,
	CurrencyType,
	HoldingEventRes,
	HoldingRes,
	HoldingSecurityRes,
	SecurityIssuedIn,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { HoldingsType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsController.tsx';
import { useMantineTableDataOperationsFromUrlParamState } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { useMantineTheme } from '@mantine/core';
import { TableData } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { getFilterableColumnIds } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';

export interface RegularViewTableItemData extends TableData {
	holderName: string;
	heldForName?: string;
	holderId: string;
	securityPersonName: string;
	securityUnitDenomination: number;
	securityIssuedIn?: SecurityIssuedIn;
	securityCurrency?: CurrencyType;
	securityName: string;
	securityId: string;
	mipName?: string;
	mipId?: string;
	unitsAmount?: number;
	unitsPercentage?: number;
	totalCost?: number;
	votesAmount?: number;
	votesPercentage?: number;
	totalNominalValue?: number;
	totalShareCapital?: number;
	totalSharePremium?: number;
	associatedBlocks?: BlockRange[];
	events?: HoldingEventRes[];
}

export interface SimpleRegularViewTableItemData {
	holderId: string;
	holderName: string;
	securityId: string;
	securityName: string;
}

export const useRegularViewTableController = ({
	showSummarized,
	showPercentages,
	tableData = [],
	tableType,
	viewByBlock,
}: {
	showSummarized: boolean;
	showPercentages: boolean;
	viewByBlock: boolean;
	tableData?: HoldingRes[];
	tableType?: HoldingsType;
}) => {
	const tCapManage = useTranslationCapManage();
	const theme = useMantineTheme();

	const [showCreateHoldingsEventDrawer, setShowCreateHoldingsEventDrawer] = useState(false);
	const [showEditHoldingsEventDrawer, setShowEditHoldingsEventDrawer] = useState(false);
	const [showDeleteHoldingsEventDrawer, setShowDeleteHoldingsEventDrawer] = useState(false);
	const [holdingsEventEdit, setHoldingsEventEdit] = useState<HoldingEventRes[]>();
	const [holding, setHolding] = useState<RegularViewTableItemData>();

	const regularViewData: RegularViewTableItemData[] = useMemo(() => {
		const flattenedData: RegularViewTableItemData[] = [];

		tableData.forEach((holding) => {
			holding.securities.forEach((security) => {
				const flattenedHolding: RegularViewTableItemData = {
					holderId: holding.holderId,
					holderName: holding.holderName ?? '',
					heldForName: holding.heldForDisplayName,
					securityId: security.securityId,
					securityName: security.name,
					mipName: holding.mipName,
					mipId: holding.mipId,
					securityUnitDenomination: security.securityUnitDenominator,
					securityPersonName: security.securityPersonName ?? '',
					securityIssuedIn: security.securityIssuedIn,
					securityCurrency: security.currency,
					unitsAmount: security.shareHolding.unitsAmount,
					unitsPercentage: security.shareHolding.unitsPercentage ?? 0,
					totalCost: security.shareHolding.cost,
					votesAmount: security.shareHolding.votesAmount,
					votesPercentage: security.shareHolding.votesPercentage ?? 0,
					totalNominalValue: security.shareHolding.totalNominalValue,
					totalSharePremium: security.shareHolding.totalSharePremium,
					totalShareCapital: security.shareHolding.totalShareCapital,
					associatedBlocks: security.associatedBlocks ?? [],
					events: security.events ?? [],
				};
				flattenedData.push(flattenedHolding);
			});
		});

		return flattenedData;
	}, [tableData, viewByBlock]);

	const debtHoldings = useMemo(() => {
		const result: SimpleRegularViewTableItemData[] = [];

		tableData.forEach((holding) => {
			holding.securities.forEach((security) => {
				if (security.type === SecurityType.LOAN || security.type === SecurityType.OTHER) {
					const flattenedHolding: SimpleRegularViewTableItemData = {
						holderId: holding.holderId,
						holderName: holding.holderName || '',
						securityId: security.securityId,
						securityName: security.name,
					};
					result.push(flattenedHolding);
				}
			});
		});

		return result;
	}, [tableData]);

	const allSecurities = useMemo(() => {
		const securityMap = new Map<string, HoldingSecurityRes>();

		tableData
			.flatMap((item) => item.securities ?? [])
			.forEach((security) => {
				if (!securityMap.has(security.securityId)) {
					securityMap.set(security.securityId, {
						...security,
						shareHolding: { ...security.shareHolding },
					});
				}
			});

		return Array.from(securityMap.values());
	}, [tableData]);

	const handleCreateEvent = useCallback((holding: RegularViewTableItemData) => {
		setHolding(holding);
		setShowCreateHoldingsEventDrawer(true);
	}, []);

	const handleEditEvent = useCallback(
		(holding: RegularViewTableItemData, events: HoldingEventRes[]) => {
			setHolding(holding);
			setHoldingsEventEdit(events);
			setShowEditHoldingsEventDrawer(true);
		},
		[]
	);

	const handleDeleteEvent = useCallback(
		(holding: RegularViewTableItemData, events: HoldingEventRes[]) => {
			setHolding(holding);
			setHoldingsEventEdit(events);
			setShowDeleteHoldingsEventDrawer(true);
		},
		[]
	);

	const columns = useMemo(
		() =>
			getRegularViewTableColumns({
				tCapManage,
				showSummarized,
				showPercentage: showPercentages,
				securitiesNames: allSecurities,
				tableType,
				onCreateEvent: handleCreateEvent,
				onEditEvent: handleEditEvent,
				onDeleteEvent: handleDeleteEvent,
				viewByBlock,
				theme,
			}),
		[
			tCapManage,
			showSummarized,
			showPercentages,
			allSecurities,
			tableType,
			handleCreateEvent,
			handleEditEvent,
			handleDeleteEvent,
			viewByBlock,
			theme,
		]
	);

	const mantineTableDataOperations = useMantineTableDataOperationsFromUrlParamState();
	const { setColumnFilters } = mantineTableDataOperations;

	useEffect(() => {
		const supportedFilterIds = new Set(getFilterableColumnIds(columns));

		setColumnFilters((currentFilters) =>
			currentFilters.some((filter) => !supportedFilterIds.has(filter.id))
				? currentFilters.filter((filter) => supportedFilterIds.has(filter.id))
				: currentFilters
		);
	}, [columns, setColumnFilters]);

	const onHoldingsCreateEventModalClose = async () => {
		setShowCreateHoldingsEventDrawer(false);
	};

	return {
		regularViewData,
		debtHoldings,
		columns,
		mantineTableDataOperations,
		onHoldingsCreateEventModalClose,
		showCreateHoldingsEventDrawer,
		setShowCreateHoldingsEventDrawer,
		showEditHoldingsEventDrawer,
		setShowEditHoldingsEventDrawer,
		holdingsEventEdit,
		setHoldingsEventEdit,
		setShowDeleteHoldingsEventDrawer,
		showDeleteHoldingsEventDrawer,
		holding,
		setHolding,
	};
};
